import type { Unsubscribe } from '@games/core'
import type { GameMessage } from '@games/protocol'
import { decodeMessage } from '@games/protocol'
import * as PeerJsModule from 'peerjs'

import { buildPeerOptions } from './ice'
import { createIceWatcher, logDiagnostics, logIce } from './ice-diagnostics'
import type { PeerConnectionLike, PeerFactory, PeerLike } from './peer-like'
import { DEFAULT_CONNECT_TIMEOUT_MS, ROOM_PREFIX, normalizeRoomCode } from './room-code'
import type { ConnectionState, Transport, TransportFactory, TransportOptions } from './transport'

type Role = 'host' | 'guest'

type MessageListener = (from: string, msg: GameMessage) => void
type PeerListener = (peerId: string) => void
type StateListener = (state: ConnectionState, error?: string) => void
type LogListener = (line: string) => void

type PeerConstructor = new (id?: string, options?: unknown) => PeerLike

let cachedDefaultFactory: PeerFactory | null = null

/**
 * 解析 peerjs 的 Peer 构造函数。
 *
 * peerjs 1.5.5 同时提供 ESM（具名导出 Peer）与 CJS（只有 default）构建，
 * 在浏览器构建、Node 测试环境里拿到的形状不一致，所以两种都兼容。
 */
function getDefaultPeerFactory(): PeerFactory {
  if (cachedDefaultFactory) return cachedDefaultFactory

  const module = PeerJsModule as unknown as {
    Peer?: PeerConstructor
    default?: PeerConstructor | { Peer?: PeerConstructor }
  }
  const fromDefault =
    typeof module.default === 'function'
      ? module.default
      : typeof module.default === 'object' && module.default !== null
        ? module.default.Peer
        : undefined
  const ctor = module.Peer ?? fromDefault

  if (!ctor) throw new Error('无法加载 peerjs 的 Peer 构造函数，请检查依赖是否安装完整。')
  cachedDefaultFactory = (id, options) => new ctor(id, options)
  return cachedDefaultFactory
}

function readString(source: unknown, key: string): string | undefined {
  if (typeof source !== 'object' || source === null) return undefined
  const value = (source as Record<string, unknown>)[key]
  return typeof value === 'string' ? value : undefined
}

function describeError(error: unknown): string {
  if (error instanceof Error) return error.message
  return typeof error === 'string' ? error : String(error)
}

function previewPayload(payload: unknown): string {
  try {
    const text = typeof payload === 'string' ? payload : JSON.stringify(payload)
    if (typeof text !== 'string') return String(payload)
    return text.length > 120 ? `${text.slice(0, 120)}…` : text
  } catch {
    return String(payload)
  }
}

function buildShareLink(roomCode: string): string {
  // location 只允许出现在这个实现里
  if (typeof location === 'undefined') return ''
  return `${location.origin}/fogchess?room=${roomCode}`
}

function createTransport(options: TransportOptions, role: Role, rawRoomCode: string): Transport {
  const roomCode = normalizeRoomCode(rawRoomCode)
  const connectTimeoutMs = options.connectTimeoutMs ?? DEFAULT_CONNECT_TIMEOUT_MS
  const writeLog = options.onLog ?? logIce
  const peerFactory = options.peerFactory ?? getDefaultPeerFactory()

  const messageListeners = new Set<MessageListener>()
  const joinListeners = new Set<PeerListener>()
  const leaveListeners = new Set<PeerListener>()
  const stateListeners = new Set<StateListener>()
  const logListeners = new Set<LogListener>()

  // 房主从「已占号、等待对手」开始；加入方从「正在连接房主」开始
  let stateValue: ConnectionState = role === 'host' ? 'signaling' : 'connecting'
  let errorValue: string | undefined
  let closed = false

  let peer: PeerLike | null = null
  /**
   * 全部连接，键是 peer id。
   * Map 的插入顺序 = 连接的先后顺序 —— 房主「先到先得」判定座位先后就靠它，
   * 所以**不要**在别处重排这个 Map。
   */
  const connections = new Map<string, PeerConnectionLike>()
  let connectTimer: ReturnType<typeof setTimeout> | null = null
  let iceWatcher: ReturnType<typeof createIceWatcher> | null = null
  let selfIdValue = role === 'host' ? ROOM_PREFIX + roomCode : ''

  function publishLog(line: string): void {
    if (closed) return
    writeLog(line)
    for (const listener of [...logListeners]) listener(line)
  }

  function notifyState(next: ConnectionState, error?: string): void {
    stateValue = next
    errorValue = error
    for (const listener of [...stateListeners]) listener(next, error)
  }

  function emitState(next: ConnectionState, error?: string): void {
    if (closed || next === stateValue) return
    notifyState(next, error)
  }

  function clearConnectTimer(): void {
    if (connectTimer === null) return
    clearTimeout(connectTimer)
    connectTimer = null
  }

  /** 取第一条连接（诊断用：ICE 统计报告一条就够，不必把每条连接都盯上）。 */
  function firstConnection(): PeerConnectionLike | null {
    for (const target of connections.values()) return target
    return null
  }

  function openConnections(): PeerConnectionLike[] {
    return [...connections.values()].filter((target) => target.open)
  }

  function startConnectTimer(): void {
    if (closed || connectTimer !== null || stateValue === 'connected') return
    connectTimer = setTimeout(() => {
      connectTimer = null
      if (closed || stateValue === 'connected') return
      publishLog('连接建立超时')
      void logDiagnostics(firstConnection()?.peerConnection, writeLog)
      emitState(
        'failed',
        '连接建立超时：双方网络可能无法直连（NAT/防火墙），请尝试更换网络或使用手机热点后重试。',
      )
    }, connectTimeoutMs)
  }

  function watchIce(target: PeerConnectionLike): void {
    if (iceWatcher) return
    iceWatcher = createIceWatcher((reason) => {
      publishLog(`ICE 失败：${reason}`)
      emitState('failed', reason)
    }, writeLog)
    iceWatcher.attach(target)
  }

  function publishMessage(from: string, payload: unknown): void {
    if (closed) return
    let message: GameMessage | null = null
    try {
      const result = decodeMessage(payload)
      message = result.ok ? result.value : null
    } catch {
      message = null
    }
    if (message === null) {
      // 畸形数据只丢弃 + 记日志，绝不中断连接
      publishLog(`收到无法解析的数据，已丢弃：${previewPayload(payload)}`)
      return
    }
    for (const listener of [...messageListeners]) listener(from, message)
  }

  function handleConnectionClosed(target: PeerConnectionLike): void {
    if (closed) return
    if (connections.get(target.peer) === target) connections.delete(target.peer)

    if (stateValue === 'connected') {
      publishLog('对方已断开连接')
      for (const listener of [...leaveListeners]) listener(target.peer)
      // 只要还有别的连接活着（典型情况：观众都还在看），房间就是「已连接」
      if (openConnections().length === 0) {
        clearConnectTimer()
        emitState('closed')
      }
      return
    }

    // 尚未 connected：只有在没有别的连接可能接上时才判失败
    if (connections.size === 0) {
      clearConnectTimer()
      emitState('failed', '连接在建立完成前已断开')
    }
  }

  function acceptConnection(target: PeerConnectionLike, side: Role): void {
    connections.set(target.peer, target)
    publishLog(side === 'host' ? `收到对方的连接请求：${target.peer}` : `正在与房主建立连接：${target.peer}`)
    // 已经连上过的房主不要因为又来了一个人而退回「正在建立通道」
    if (side === 'host' && stateValue !== 'connected') emitState('connecting')
    watchIce(target)

    target.on('open', () => {
      if (closed) return
      clearConnectTimer()
      emitState('connected')
      publishLog('数据通道已打开')
      for (const listener of [...joinListeners]) listener(target.peer)
    })
    target.on('data', (payload) => {
      publishMessage(target.peer, payload)
    })
    target.on('error', (payload) => {
      publishLog(`数据通道错误：${readString(payload, 'type') ?? describeError(payload)}`)
    })
    target.on('close', () => {
      handleConnectionClosed(target)
    })
  }

  function handleIncomingConnection(payload: unknown): void {
    if (closed) return
    const target = payload as PeerConnectionLike | null
    if (!target || typeof target.send !== 'function') {
      publishLog('收到无效的连接请求，已忽略')
      return
    }
    const peerId = typeof target.peer === 'string' ? target.peer : ''
    if (peerId === '') {
      publishLog('连接请求缺少 peer id，已忽略')
      return
    }

    // 同一个 peer id 重复连入：关掉旧的，避免 Map 里留下关不掉的孤儿连接
    const existing = connections.get(peerId)
    if (existing && existing !== target) {
      publishLog(`同一 peer 重复接入，关闭旧连接：${peerId}`)
      try {
        existing.close()
      } catch {
        // 旧连接可能已经断了
      }
    }

    // 座位/观众身份由房间层决定，传输层来者不拒（观众数不设上限）
    acceptConnection(target, 'host')
  }

  function dialHost(target: PeerLike): void {
    const hostId = ROOM_PREFIX + roomCode
    let connection: PeerConnectionLike
    try {
      connection = target.connect(hostId, { reliable: true })
    } catch (error) {
      emitState('failed', `连接失败：${describeError(error)}`)
      return
    }
    if (closed) {
      try {
        connection.close()
      } catch {
        // 忽略
      }
      return
    }
    acceptConnection(connection, 'guest')
    startConnectTimer()
  }

  function handlePeerError(payload: unknown): void {
    const type = readString(payload, 'type') ?? 'unknown'
    publishLog(`peer 错误：${type} ${readString(payload, 'message') ?? ''}`.trim())
    if (closed) return
    if (type === 'peer-unavailable') {
      emitState('failed', '房间不存在或已关闭。')
      return
    }
    emitState(
      'failed',
      role === 'host' ? `创建对局失败：${type}，请刷新重试。` : `连接失败：${type}`,
    )
  }

  function attachPeer(target: PeerLike): void {
    target.on('open', () => {
      if (closed) return
      selfIdValue = target.id
      publishLog(`信令服务器已连接，peer id=${target.id}`)
      if (role === 'host') {
        emitState('signaling')
        return
      }
      dialHost(target)
    })
    target.on('connection', (payload) => {
      if (role !== 'host') return
      handleIncomingConnection(payload)
    })
    target.on('error', (payload) => {
      handlePeerError(payload)
    })
    target.on('disconnected', () => {
      publishLog('与信令服务器断开连接，PeerJS 会尝试自动重连')
    })
    target.on('close', () => {
      if (closed) return
      publishLog('信令连接已关闭')
      emitState('closed')
    })
  }

  async function start(): Promise<void> {
    publishLog(role === 'host' ? `创建对局，房间号=${roomCode}` : `加入对局，房间号=${roomCode}`)

    let peerOptions: { config: { iceServers: RTCIceServer[] } }
    try {
      peerOptions = await buildPeerOptions(options)
    } catch {
      // buildPeerOptions 自身不会抛异常，这里只是兜底，保证建连流程不因取凭证而中断
      peerOptions = { config: { iceServers: [] } }
    }
    if (closed) return

    try {
      peer = peerFactory(role === 'host' ? ROOM_PREFIX + roomCode : undefined, peerOptions)
    } catch (error) {
      emitState('failed', `创建连接失败：${describeError(error)}`)
      return
    }
    attachPeer(peer)
  }

  function sendTo(target: PeerConnectionLike, msg: GameMessage): void {
    if (!target.open) {
      publishLog('数据通道尚未打开，消息未发送')
      return
    }
    try {
      target.send(msg)
    } catch (error) {
      publishLog(`发送失败：${describeError(error)}`)
    }
  }

  function send(to: string | '*', msg: GameMessage): void {
    if (closed) return
    if (to === '*') {
      // 广播给除自己外的全部连接：房主中继靠的就是这条
      for (const target of openConnections()) sendTo(target, msg)
      return
    }
    const target = connections.get(to)
    // 目标不在连接表里就静默忽略，绝不抛异常
    if (!target) return
    sendTo(target, msg)
  }

  function close(): void {
    if (closed) return
    closed = true
    clearConnectTimer()

    const targets = [...connections.values()]
    connections.clear()
    for (const target of targets) {
      try {
        target.close()
      } catch {
        // 已经关闭时忽略
      }
    }

    const activePeer = peer
    peer = null
    if (activePeer) {
      try {
        activePeer.destroy()
      } catch {
        // 已经销毁时忽略
      }
    }

    iceWatcher = null
    notifyState('closed')
    messageListeners.clear()
    joinListeners.clear()
    leaveListeners.clear()
    stateListeners.clear()
    logListeners.clear()
  }

  const transport: Transport = {
    get selfId(): string {
      return selfIdValue
    },
    get peers(): readonly string[] {
      return openConnections().map((target) => target.peer)
    },
    get state(): ConnectionState {
      return stateValue
    },
    get roomCode(): string {
      return roomCode
    },
    get shareLink(): string {
      return role === 'host' ? buildShareLink(roomCode) : ''
    },
    send,
    onMessage(cb: MessageListener): Unsubscribe {
      messageListeners.add(cb)
      return () => {
        messageListeners.delete(cb)
      }
    },
    onPeerJoin(cb: PeerListener): Unsubscribe {
      joinListeners.add(cb)
      return () => {
        joinListeners.delete(cb)
      }
    },
    onPeerLeave(cb: PeerListener): Unsubscribe {
      leaveListeners.add(cb)
      return () => {
        leaveListeners.delete(cb)
      }
    },
    onStateChange(cb: StateListener): Unsubscribe {
      stateListeners.add(cb)
      // 订阅时立即用当前状态回调一次，订阅方不必自己补一次初始渲染
      if (!closed) cb(stateValue, errorValue)
      return () => {
        stateListeners.delete(cb)
      }
    },
    onLog(cb: LogListener): Unsubscribe {
      logListeners.add(cb)
      return () => {
        logListeners.delete(cb)
      }
    },
    close,
  }

  if (role === 'guest') {
    // 加入方的整段「加入」过程都要有上限：信令连不上、房主不在、通道打不开都算超时
    startConnectTimer()
  }

  void start()

  return transport
}

/** 创建基于 PeerJS 的传输层工厂。 */
export function createPeerJsTransport(options: TransportOptions = {}): TransportFactory {
  return {
    createHost({ roomCode }): Transport {
      return createTransport(options, 'host', roomCode)
    },
    createGuest({ roomCode }): Transport {
      return createTransport(options, 'guest', roomCode)
    },
  }
}

